import { useState, useEffect } from 'react';
import { chainAPI } from '../lib/api';
import { Trash2, ToggleLeft, ToggleRight, BookOpen, AlertTriangle } from 'lucide-react';
import MediaManager from './MediaManager';
import AccountabilityDashboard from './AccountabilityDashboard';
import LaunchConfigPanel from './LaunchConfigPanel';

function ChainManager() {
  const [chains, setChains] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchChains = () => chainAPI.list().then(r => { setChains(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { fetchChains(); }, []);

  const handleToggle = async (chainId) => {
    await chainAPI.toggleContradiction(chainId);
    fetchChains();
  };

  const handleDelete = async (chainId) => {
    if (!window.confirm('Delete this chain?')) return;
    await chainAPI.delete(chainId);
    fetchChains();
  };

  if (loading) return <div className="font-hud text-[#2F8A92] text-sm tracking-wider animate-pulse">LOADING...</div>;

  return (
    <div className="space-y-3" data-testid="admin-chain-manager">
      <h3 className="font-hud text-[#2F8A92] text-sm tracking-widest uppercase flex items-center gap-2">
        <BookOpen size={14} /> CHAIN MANAGEMENT
      </h3>
      {chains.map(chain => (
        <div key={chain.id} className="glass-panel p-4 group" data-testid={`admin-chain-${chain.id}`}>
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-historical text-[#D4A84A] text-sm">{chain.subject}</h4>
              <p className="font-mono text-[10px] text-[#52525B] mt-1">{chain.description}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                data-testid={`toggle-contradiction-${chain.id}`}
                onClick={() => handleToggle(chain.id)}
                className={`flex items-center gap-1 font-hud text-[10px] tracking-wider transition-all duration-300 ${
                  chain.has_contradiction ? 'text-[#FF4444]' : 'text-[#52525B]'
                }`}
                title="Toggle contradiction"
              >
                {chain.has_contradiction ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                <span className="hidden md:inline">CONTRADICTION</span>
              </button>
              <button
                data-testid={`admin-delete-chain-${chain.id}`}
                onClick={() => handleDelete(chain.id)}
                className="text-[#52525B] hover:text-red-400 transition-all duration-300"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
          <div className="mt-2 flex items-center gap-4 font-hud text-[10px] text-white/80 tracking-wider">
            <span>ORIGIN: {chain.origin?.title}</span>
            {chain.has_contradiction && (
              <span className="text-[#FF4444] flex items-center gap-1"><AlertTriangle size={10} /> HAS CONTRADICTION</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminPanel({ onViewChange }) {
  return (
    <div className="p-6 space-y-8" data-testid="admin-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-historical text-[#D4A84A] text-2xl md:text-3xl lg:text-4xl font-light tracking-tight">
            Founder Admin
          </h2>
          <p className="font-hud text-[10px] md:text-xs text-white/80 tracking-wider mt-1">
            REVIEW QUEUE · CHAINS · MEDIA · LAUNCH ARC · ACCOUNTABILITY
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="open-pipeline-queue-btn"
            onClick={() => onViewChange && onViewChange('pipeline-queue')}
            className="font-hud text-xs md:text-sm tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-4 py-2 hover:bg-[#D4A84A]/85 inline-flex items-center gap-2"
          >
            PIPELINE QUEUE
          </button>
          <button
            data-testid="open-pipeline-ops-btn"
            onClick={() => onViewChange && onViewChange('pipeline-ops')}
            className="font-hud text-xs md:text-sm tracking-widest text-white/85 border border-[#D4A84A]/40 px-4 py-2 hover:text-[#D4A84A] inline-flex items-center gap-2"
          >
            PIPELINE OPS
          </button>
          <button
            data-testid="open-review-queue-btn"
            onClick={() => onViewChange && onViewChange('review-queue')}
            className="font-hud text-xs md:text-sm tracking-widest text-white/85 border border-[#D4A84A]/40 px-4 py-2 hover:text-[#D4A84A] inline-flex items-center gap-2"
          >
            LEAD REVIEW QUEUE
          </button>
          <button
            data-testid="open-integrity-btn"
            onClick={() => onViewChange && onViewChange('integrity-dashboard')}
            className="font-hud text-xs md:text-sm tracking-widest text-white/85 border border-[#D4A84A]/40 px-4 py-2 hover:text-[#D4A84A]"
          >
            INTEGRITY FLAGS
          </button>
          <button
            data-testid="open-lessons-contest-btn"
            onClick={() => onViewChange && onViewChange('lessons-contest')}
            className="font-hud text-xs md:text-sm tracking-widest text-white/85 border border-[#2F8A92]/40 px-4 py-2 hover:text-[#2F8A92]"
          >
            LESSONS CONTEST
          </button>
        </div>
      </div>
      <LaunchConfigPanel />
      <ChainManager />
      <MediaManager />
      <AccountabilityDashboard />
    </div>
  );
}
